import React, { useCallback, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import axios from 'axios';
import { toast } from 'sonner';
import 'iconify-icon';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const uploadFile = async (file, folder = 'blog') => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('folder', folder);
  const { data } = await axios.post(`${API_URL}/admin/media`, fd, {
    withCredentials: true,
    headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
  });
  const backendBase = process.env.REACT_APP_BACKEND_URL;
  return data.url.startsWith('http') ? data.url : `${backendBase}${data.url}`;
};

const ToolbarBtn = ({ active, onClick, icon, title, testId }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    data-testid={testId}
    className={`w-8 h-8 flex items-center justify-center rounded text-sm transition-colors ${
      active
        ? 'bg-accent-orange text-white'
        : 'text-neutral-700 hover:bg-neutral-100'
    }`}
  >
    <iconify-icon icon={icon}></iconify-icon>
  </button>
);

/**
 * Rich-text editor based on TipTap.
 * - Bold / Italic / Headings / Lists / Quote / Link / Image
 * - Drag-and-drop or paste images directly into content (auto-uploads)
 * - Outputs HTML via onChange(html)
 */
const RichTextEditor = ({ value, onChange, folder = 'blog', placeholder = 'Escreva o conteúdo…' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({ inline: false, allowBase64: false }),
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none min-h-[300px] focus:outline-none px-4 py-3 text-neutral-800',
        'data-testid': 'rich-editor-content',
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) {
              event.preventDefault();
              uploadFile(file, folder)
                .then((url) => {
                  view.dispatch(
                    view.state.tr.replaceSelectionWith(
                      view.state.schema.nodes.image.create({ src: url, alt: file.name }),
                    ),
                  );
                  toast.success('Imagem inserida');
                })
                .catch(() => toast.error('Falha ao enviar imagem'));
              return true;
            }
          }
        }
        return false;
      },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (!files || !files.length) return false;
        const file = files[0];
        if (!file.type.startsWith('image/')) return false;
        event.preventDefault();
        uploadFile(file, folder)
          .then((url) => {
            const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });
            const tr = view.state.tr.insert(
              pos ? pos.pos : view.state.selection.from,
              view.state.schema.nodes.image.create({ src: url, alt: file.name }),
            );
            view.dispatch(tr);
            toast.success('Imagem inserida');
          })
          .catch(() => toast.error('Falha ao enviar imagem'));
        return true;
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
  });

  // Sync external value changes (e.g. resetting form)
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  const insertImageFromFile = useCallback(async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file || !editor) return;
      try {
        const url = await uploadFile(file, folder);
        editor.chain().focus().setImage({ src: url, alt: file.name }).run();
        toast.success('Imagem inserida');
      } catch {
        toast.error('Falha ao enviar imagem');
      }
    };
    input.click();
  }, [editor, folder]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href;
    const url = window.prompt('URL do link:', previous || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank' }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border border-neutral-300 rounded-lg overflow-hidden bg-white">
      <div className="flex flex-wrap items-center gap-1 px-2 py-2 border-b border-neutral-200 bg-neutral-50">
        <ToolbarBtn
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          icon="solar:text-bold-bold"
          title="Negrito (Ctrl+B)"
          testId="editor-bold"
        />
        <ToolbarBtn
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          icon="solar:text-italic-bold"
          title="Itálico (Ctrl+I)"
          testId="editor-italic"
        />
        <span className="w-px h-5 bg-neutral-300 mx-1"></span>
        <ToolbarBtn
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          icon="solar:document-text-linear"
          title="Título"
          testId="editor-h2"
        />
        <ToolbarBtn
          active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          icon="solar:document-linear"
          title="Subtítulo"
          testId="editor-h3"
        />
        <span className="w-px h-5 bg-neutral-300 mx-1"></span>
        <ToolbarBtn
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          icon="solar:list-linear"
          title="Lista"
          testId="editor-ul"
        />
        <ToolbarBtn
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          icon="solar:list-arrow-down-linear"
          title="Lista numerada"
          testId="editor-ol"
        />
        <ToolbarBtn
          active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          icon="solar:chat-square-linear"
          title="Citação"
          testId="editor-quote"
        />
        <span className="w-px h-5 bg-neutral-300 mx-1"></span>
        <ToolbarBtn
          active={editor.isActive('link')}
          onClick={setLink}
          icon="solar:link-linear"
          title="Link"
          testId="editor-link"
        />
        <ToolbarBtn
          onClick={insertImageFromFile}
          icon="solar:gallery-add-linear"
          title="Inserir imagem"
          testId="editor-image"
        />
        <span className="flex-1"></span>
        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-700 mr-2">
          Cole ou arraste imagens
        </span>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
